/**
 * World Weather Extension for Scratch
 *
 * Provides blocks for fetching real-time weather data, air pollution,
 * and geocoding information from the OpenWeatherMap API.
 *
 * API Reference: https://openweathermap.org/api
 *
 * scratch-vm v5.0.262 compatibility:
 *   - Valid BlockTypes: command, reporter, Boolean, hat, event, conditional, loop, button
 *   - 'label' is NOT valid — use '---' string separators
 *   - 'button' only supports 3 hardcoded callbacks — use 'command' for custom actions
 */
import formatMessage from 'format-message';

const WEATHER_API_BASE = 'https://api.openweathermap.org';

// Block & category colours (dark-teal/green)
const COLOR_PRIMARY = '#0E4D45';
const COLOR_SECONDARY = '#0A3B34';
const COLOR_TERTIARY = '#072C27';

class WorldWeatherExtension {
    constructor (runtime) {
        this.runtime = runtime;

        // API key (set by the user via the "set API key" block)
        this._apiKey = '';

        // ── Cached API results ────────────────────────────────
        this._weatherData = {};
        this._pollutionData = {};
        this._locationData = {};
    }

    // ── Scratch extension entry-point ─────────────────────────

    getInfo () {
        return {
            id: 'worldWeather',
            name: formatMessage({
                id: 'gui.extension.worldWeather.name',
                default: 'World Weather'
            }),
            color1: COLOR_PRIMARY,
            color2: COLOR_SECONDARY,
            color3: COLOR_TERTIARY,

            blocks: [
                // ═══════════════════════════════════════════
                //  SIGN-IN / API KEY
                // ═══════════════════════════════════════════
                {
                    opcode: 'openSignInPage',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'worldWeather.blocks.openSignin',
                        default: 'open OpenWeatherMap sign-in page'
                    })
                },
                {
                    opcode: 'setApiKey',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'worldWeather.blocks.setApiKey',
                        default: 'set open weather map API key [KEY]'
                    }),
                    arguments: {
                        KEY: {type: 'string', defaultValue: 'your api key'}
                    }
                },

                // ═══════════════════════════════════════════
                //  WEATHER — fetch + results together
                // ═══════════════════════════════════════════
                '---',
                {
                    opcode: 'getWeatherByZip',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'worldWeather.blocks.getWeatherZip',
                        default: 'get weather data using zip [ZIP] and country code [CC]'
                    }),
                    arguments: {
                        ZIP: {type: 'string', defaultValue: '110001'},
                        CC: {type: 'string', defaultValue: 'IN'}
                    }
                },
                {
                    opcode: 'getWeatherByCoords',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'worldWeather.blocks.getWeatherCoords',
                        default: 'get weather data using lat: [LAT] and long: [LON]'
                    }),
                    arguments: {
                        LAT: {type: 'number', defaultValue: 28.6139},
                        LON: {type: 'number', defaultValue: 77.2088}
                    }
                },

                {
                    opcode: 'currentTemp',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.temperature',
                        default: 'temperature'
                    })
                },
                {
                    opcode: 'feelsLikeTemp',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.feelsLike',
                        default: 'feels like'
                    })
                },
                {
                    opcode: 'tempMin',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.tempMin',
                        default: 'temp min'
                    })
                },
                {
                    opcode: 'tempMax',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.tempMax',
                        default: 'temp max'
                    })
                },
                {
                    opcode: 'currentHumidity',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.humidity',
                        default: 'humidity'
                    })
                },
                {
                    opcode: 'currentPressure',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.pressure',
                        default: 'pressure'
                    })
                },
                {
                    opcode: 'currentVisibility',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.visibility',
                        default: 'visibility (km)'
                    })
                },
                {
                    opcode: 'currentWeather',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.description',
                        default: 'weather description'
                    })
                },
                {
                    opcode: 'currentWindSpeed',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.windSpeed',
                        default: 'wind speed'
                    })
                },
                {
                    opcode: 'currentWindDirection',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.windDirection',
                        default: 'wind direction'
                    })
                },
                {
                    opcode: 'currentWindGust',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.windGust',
                        default: 'wind gust'
                    })
                },
                {
                    opcode: 'currentCloudCoverage',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.cloudCoverage',
                        default: 'cloud coverage %'
                    })
                },
                {
                    opcode: 'sunriseTime',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.sunrise',
                        default: 'sunrise time'
                    })
                },
                {
                    opcode: 'sunsetTime',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.sunset',
                        default: 'sunset time'
                    })
                },
                {
                    opcode: 'weatherCountry',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.countryCode',
                        default: 'weather country code'
                    })
                },

                // ═══════════════════════════════════════════
                //  AIR POLLUTION — fetch + results together
                // ═══════════════════════════════════════════
                '---',
                {
                    opcode: 'getAirPollution',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'worldWeather.blocks.getAirPollution',
                        default: 'get air pollution using lat: [LAT] and long: [LON]'
                    }),
                    arguments: {
                        LAT: {type: 'number', defaultValue: 28.6139},
                        LON: {type: 'number', defaultValue: 77.2088}
                    }
                },
                {
                    opcode: 'airQualityIndex',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.aqi',
                        default: 'air quality index'
                    })
                },
                {
                    opcode: 'coConcentration',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.co',
                        default: 'carbon monoxide (CO)'
                    })
                },
                {
                    opcode: 'noConcentration',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.no',
                        default: 'nitrogen monoxide (NO)'
                    })
                },
                {
                    opcode: 'no2Concentration',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.no2',
                        default: 'nitrogen dioxide (NO₂)'
                    })
                },
                {
                    opcode: 'o3Concentration',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.o3',
                        default: 'ozone (O₃)'
                    })
                },
                {
                    opcode: 'so2Concentration',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.so2',
                        default: 'sulfur dioxide (SO₂)'
                    })
                },
                {
                    opcode: 'pm25Concentration',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.pm25',
                        default: 'PM 2.5'
                    })
                },
                {
                    opcode: 'pm10Concentration',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.pm10',
                        default: 'PM 10'
                    })
                },
                {
                    opcode: 'nh3Concentration',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.nh3',
                        default: 'ammonia (NH₃)'
                    })
                },

                // ═══════════════════════════════════════════
                //  GEOCODING — all commands + results together
                // ═══════════════════════════════════════════
                '---',
                {
                    opcode: 'getCoordsByCity',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'worldWeather.blocks.getCoordsCity',
                        default: 'get coordinates using city name [CITY] and country code [CC]'
                    }),
                    arguments: {
                        CITY: {type: 'string', defaultValue: 'New Delhi'},
                        CC: {type: 'string', defaultValue: 'IN'}
                    }
                },
                {
                    opcode: 'getCoordsByZip',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'worldWeather.blocks.getCoordsZip',
                        default: 'get coordinates using zip code [ZIP] and country code [CC]'
                    }),
                    arguments: {
                        ZIP: {type: 'string', defaultValue: '110001'},
                        CC: {type: 'string', defaultValue: 'IN'}
                    }
                },
                {
                    opcode: 'getLocationByCoords',
                    blockType: 'command',
                    text: formatMessage({
                        id: 'worldWeather.blocks.getLocation',
                        default: 'get location name using lat: [LAT] and long: [LON]'
                    }),
                    arguments: {
                        LAT: {type: 'number', defaultValue: 28.6139},
                        LON: {type: 'number', defaultValue: 77.2088}
                    }
                },
                {
                    opcode: 'cityName',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.cityName',
                        default: 'city name'
                    })
                },
                {
                    opcode: 'locationName',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.locationName',
                        default: 'location name'
                    })
                },
                {
                    opcode: 'locationZipCode',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.zipCode',
                        default: 'zip code'
                    })
                },
                {
                    opcode: 'locationState',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.stateName',
                        default: 'state name'
                    })
                },
                {
                    opcode: 'locationCountryCode',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.locationCountryCode',
                        default: 'location country code'
                    })
                },
                {
                    opcode: 'locationLatitude',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.latitude',
                        default: 'latitude'
                    })
                },
                {
                    opcode: 'locationLongitude',
                    blockType: 'reporter',
                    text: formatMessage({
                        id: 'worldWeather.reporters.longitude',
                        default: 'longitude'
                    })
                }
            ]
        };
    }

    // ── Helpers ────────────────────────────────────────────────

    /**
     * Safe value getter — avoids the `0 || ''` falsy bug.
     * Returns '' only when the value is null or undefined.
     */
    _val (obj, key) {
        if (!obj) return '';
        const v = obj[key];
        return (v !== null && v !== undefined) ? v : '';
    }

    /**
     * Internal fetch wrapper — returns parsed JSON or null on error.
     */
    _fetch (url) {
        return fetch(url)
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .catch(err => {
                // eslint-disable-next-line no-console
                console.warn('[WorldWeather]', err);
                return null;
            });
    }

    /**
     * Convert a UNIX timestamp to a human-readable local time string.
     */
    _unixToTime (unix, timezoneOffset) {
        const d = new Date((unix + (timezoneOffset || 0)) * 1000);
        const hours = d.getUTCHours().toString()
            .padStart(2, '0');
        const mins = d.getUTCMinutes().toString()
            .padStart(2, '0');
        return `${hours}:${mins}`;
    }

    /**
     * After weather data is fetched, automatically fetch air pollution
     * and reverse-geocoding using the coordinates from the weather response.
     * This ensures all reporter blocks have data after a single weather fetch.
     */
    _autoFetchRelated () {
        const lat = this._weatherData.coord && this._weatherData.coord.lat;
        const lon = this._weatherData.coord && this._weatherData.coord.lon;
        if (lat === undefined || lon === undefined) return Promise.resolve();

        const pollutionUrl = `${WEATHER_API_BASE}/data/2.5/air_pollution?lat=${lat}&lon=${lon}&appid=${this._apiKey}`;
        const geoUrl = `${WEATHER_API_BASE}/geo/1.0/reverse?lat=${lat}&lon=${lon}&limit=1&appid=${this._apiKey}`;

        return Promise.all([
            this._fetch(pollutionUrl).then(data => {
                if (data && data.list && data.list.length) {
                    this._pollutionData = data.list[0];
                }
            }),
            this._fetch(geoUrl).then(data => {
                if (data && data.length) {
                    this._locationData = data[0];
                }
            })
        ]);
    }

    // ── Block Implementations ─────────────────────────────────

    // ─── Sign-in / API key ─────────────────────────────────────

    openSignInPage () {
        window.open('https://home.openweathermap.org/users/sign_in', '_blank');
    }

    setApiKey (args) {
        this._apiKey = args.KEY;
    }

    // ─── Weather fetchers ──────────────────────────────────────
    // Both fetchers auto-chain air pollution + geocoding calls.

    getWeatherByZip (args) {
        // Store the zip code the user provided (reverse geocoding doesn't return it)
        this._userZip = args.ZIP;
        const url = `${WEATHER_API_BASE}/data/2.5/weather?zip=${encodeURIComponent(args.ZIP)},${encodeURIComponent(args.CC)}&appid=${this._apiKey}&units=metric`;
        return this._fetch(url).then(data => {
            if (data) {
                this._weatherData = data;
                return this._autoFetchRelated();
            }
        });
    }

    getWeatherByCoords (args) {
        const url = `${WEATHER_API_BASE}/data/2.5/weather?lat=${args.LAT}&lon=${args.LON}&appid=${this._apiKey}&units=metric`;
        return this._fetch(url).then(data => {
            if (data) {
                this._weatherData = data;
                return this._autoFetchRelated();
            }
        });
    }

    // ─── Weather reporters ─────────────────────────────────────

    cityName () {
        return this._val(this._weatherData, 'name');
    }

    currentTemp () {
        return this._val(this._weatherData.main, 'temp');
    }

    feelsLikeTemp () {
        return this._val(this._weatherData.main, 'feels_like');
    }

    tempMin () {
        return this._val(this._weatherData.main, 'temp_min');
    }

    tempMax () {
        return this._val(this._weatherData.main, 'temp_max');
    }

    currentHumidity () {
        return this._val(this._weatherData.main, 'humidity');
    }

    currentPressure () {
        return this._val(this._weatherData.main, 'pressure');
    }

    currentVisibility () {
        const v = this._weatherData.visibility;
        if (v !== null && v !== undefined) {
            return (v / 1000).toFixed(1);
        }
        return '';
    }

    currentWeather () {
        if (this._weatherData.weather && this._weatherData.weather.length) {
            return this._weatherData.weather[0].description;
        }
        return '';
    }

    currentWindSpeed () {
        return this._val(this._weatherData.wind, 'speed');
    }

    currentWindDirection () {
        return this._val(this._weatherData.wind, 'deg');
    }

    currentWindGust () {
        return this._val(this._weatherData.wind, 'gust');
    }

    currentCloudCoverage () {
        return this._val(this._weatherData.clouds, 'all');
    }

    sunriseTime () {
        if (this._weatherData.sys && this._weatherData.sys.sunrise) {
            return this._unixToTime(
                this._weatherData.sys.sunrise,
                this._weatherData.timezone
            );
        }
        return '';
    }

    sunsetTime () {
        if (this._weatherData.sys && this._weatherData.sys.sunset) {
            return this._unixToTime(
                this._weatherData.sys.sunset,
                this._weatherData.timezone
            );
        }
        return '';
    }

    weatherCountry () {
        return this._val(this._weatherData.sys, 'country');
    }

    // ─── Air pollution fetcher ─────────────────────────────────

    getAirPollution (args) {
        const url = `${WEATHER_API_BASE}/data/2.5/air_pollution?lat=${args.LAT}&lon=${args.LON}&appid=${this._apiKey}`;
        return this._fetch(url).then(data => {
            if (data && data.list && data.list.length) {
                this._pollutionData = data.list[0];
            }
        });
    }

    // ─── Air pollution reporters ───────────────────────────────

    airQualityIndex () {
        return this._val(this._pollutionData.main, 'aqi');
    }

    coConcentration () {
        return this._val(this._pollutionData.components, 'co');
    }

    noConcentration () {
        return this._val(this._pollutionData.components, 'no');
    }

    no2Concentration () {
        return this._val(this._pollutionData.components, 'no2');
    }

    o3Concentration () {
        return this._val(this._pollutionData.components, 'o3');
    }

    so2Concentration () {
        return this._val(this._pollutionData.components, 'so2');
    }

    pm25Concentration () {
        return this._val(this._pollutionData.components, 'pm2_5');
    }

    pm10Concentration () {
        return this._val(this._pollutionData.components, 'pm10');
    }

    nh3Concentration () {
        return this._val(this._pollutionData.components, 'nh3');
    }

    // ─── Geocoding / Reverse-geocoding ─────────────────────────

    getCoordsByCity (args) {
        const url = `${WEATHER_API_BASE}/geo/1.0/direct?q=${encodeURIComponent(args.CITY)},${encodeURIComponent(args.CC)}&limit=1&appid=${this._apiKey}`;
        return this._fetch(url).then(data => {
            if (data && data.length) {
                this._locationData = data[0];
            }
        });
    }

    getCoordsByZip (args) {
        const url = `${WEATHER_API_BASE}/geo/1.0/zip?zip=${encodeURIComponent(args.ZIP)},${encodeURIComponent(args.CC)}&appid=${this._apiKey}`;
        return this._fetch(url).then(data => {
            if (data) this._locationData = data;
        });
    }

    getLocationByCoords (args) {
        const url = `${WEATHER_API_BASE}/geo/1.0/reverse?lat=${args.LAT}&lon=${args.LON}&limit=1&appid=${this._apiKey}`;
        return this._fetch(url).then(data => {
            if (data && data.length) {
                this._locationData = data[0];
            }
        });
    }

    // ─── Location reporters ────────────────────────────────────

    locationName () {
        return this._val(this._locationData, 'name');
    }

    locationZipCode () {
        // Reverse geocoding doesn't return zip — use stored user zip or zip geocoding result
        return this._val(this._locationData, 'zip') || this._userZip || '';
    }

    locationState () {
        return this._val(this._locationData, 'state');
    }

    locationCountryCode () {
        return this._val(this._locationData, 'country');
    }

    locationLatitude () {
        return this._val(this._locationData, 'lat');
    }

    locationLongitude () {
        return this._val(this._locationData, 'lon');
    }
}

export default WorldWeatherExtension;
